import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBillWave,
  faCreditCard,
  faTimesCircle,
  faPaw,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import api from "../../../api/api";

const WalkPaymentMethodSelection = ({ walk, isOpen, onCancel, asDiv = false ,onSave}) => {
  const [selectedMethod, setSelectedMethod] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inlinePaymentLink, setInlinePaymentLink] = useState(null);

  const paymentMethods = [
    { label: "Cash on Delivery", key: "Cash on Delivery", icon: faMoneyBillWave },
    { label: "Credit/Debit Card", key: "MamoPay", icon: faCreditCard },
  ];

  const closeModal = () => {
    setSelectedMethod("");
    setInlinePaymentLink(null);
    onCancel();
  };

  const handleConfirm = async () => {
    if (!selectedMethod) {
      toast.error("Please select a payment method.");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await api.post(`/walks/${walk.id}`, {
        _method: "PUT",
        updatePaymentMethod: 1,
        payment_method: selectedMethod,
      });

      if (selectedMethod !== "MamoPay") {
        toast.success("Payment method updated successfully.");
      }

      if (selectedMethod === "MamoPay" && response.data?.mamopay_payment_link) {
        setInlinePaymentLink(response.data.mamopay_payment_link);
      } else {
        onSave()
        closeModal();
      }
    } catch (error) {
      toast.error("Failed to update payment method. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /*** Conditional Wrapper ***/
  const Wrapper = asDiv ? "div" : Transition;
  const wrapperProps = asDiv ? {} : { appear: true, show: isOpen, as: Fragment };

  return (
    <Wrapper {...wrapperProps}>
      {asDiv ? (
        <div className="w-full max-w-7xl rounded-xl bg-bodyMain px-3 sm:px-4">
          <PaymentContent
            selectedMethod={selectedMethod}
            setSelectedMethod={setSelectedMethod}
            handleConfirm={handleConfirm}
            isSubmitting={isSubmitting}
            inlinePaymentLink={inlinePaymentLink}
            closeModal={closeModal}
            paymentMethods={paymentMethods}
          />
        </div>
      ) : (
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl rounded-xl bg-bodyMain p-4 sm:p-8 shadow-2xl overflow-y-auto max-h-[90vh]">
                <PaymentContent
                  selectedMethod={selectedMethod}
                  setSelectedMethod={setSelectedMethod}
                  handleConfirm={handleConfirm}
                  isSubmitting={isSubmitting}
                  inlinePaymentLink={inlinePaymentLink}
                  closeModal={closeModal}
                  paymentMethods={paymentMethods}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      )}
    </Wrapper>
  );
};

/*** Extracted Payment Content (for reusability) ***/
const PaymentContent = ({ selectedMethod, setSelectedMethod, handleConfirm, isSubmitting, inlinePaymentLink, closeModal, paymentMethods }) => (
  <>
    <div className="flex justify-between items-center mb-3 sm:mb-6 relative">
      <h3 className="text-lg sm:text-3xl font-bold text-purple-700 text-center w-full px-4 sm:px-8">
        Secure Your Pet's Walk with a Payment 🐶💳
      </h3>
      <button 
        onClick={closeModal} 
        className="text-gray-400 hover:text-gray-600 absolute -right-1 sm:right-0 top-1/2 -translate-y-1/2 p-2"
      >
        <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4 sm:w-6 sm:h-6" />
      </button>
    </div>

    {!inlinePaymentLink ? (
      <div className="space-y-3 sm:space-y-6">
        <p className="text-gray-600 text-center text-sm sm:text-lg px-2">
          Choose a payment method to proceed with booking your pet's adventure! 🦮🐾
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 max-w-4xl mx-auto">
          {paymentMethods.map((method) => (
            <button
              type='button'
              key={method.label}
              onClick={() => setSelectedMethod(method.key)}
              className={`flex flex-col items-center justify-center p-3 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-all ${
                selectedMethod === method.key
                  ? "bg-primary text-white"
                  : "bg-purple-100 text-gray-800 hover:bg-purple-200"
              }`}
            >
              <FontAwesomeIcon icon={method.icon} className="text-2xl sm:text-5xl mb-2 sm:mb-3" />
              <span className="text-sm sm:text-xl font-semibold">{method.label}</span>
              {selectedMethod === method.key && (
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  className="text-white text-lg sm:text-2xl mt-2 sm:mt-3" 
                />
              )}
            </button>
          ))}
        </div>

        <div className="bg-yellow-100 border-l-4 border-yellow-500 p-3 sm:p-4 rounded-md flex items-start gap-2 sm:gap-3">
          <FontAwesomeIcon 
            icon={faPaw} 
            className="text-yellow-600 text-lg sm:text-2xl mt-1 flex-shrink-0" 
          />
          <span className="text-xs sm:text-base">
            <strong>Important Notice:</strong> To proceed further, please select a payment method.
            We accept <strong>MamoPay</strong> for quick digital payments and <strong>Cash on Delivery</strong> if you prefer to pay later.
          </span>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-6 mt-4 sm:mt-6">
          <button 
            onClick={closeModal} 
            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm sm:text-base font-medium" 
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedMethod || isSubmitting}
            className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-white text-sm sm:text-base font-medium ${
              isSubmitting ? "bg-gray-400" : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            {isSubmitting ? "Processing..." : "Confirm & Proceed"}
          </button>
        </div>
      </div>
    ) : (
      <div className="mt-3 sm:mt-6">
        <h4 className="text-lg sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4 text-center px-2">
          Complete Your Payment and Get Ready for Tail Wags! 🐕💳
        </h4>
        <iframe 
          src={inlinePaymentLink} 
          title="MamoPay Payment" 
          width="100%" 
          height="400vh" 
          className="mt-2 sm:mt-0 rounded-lg" 
        />
      </div>
    )}
  </>
);

export default WalkPaymentMethodSelection;